import {
  Button,
  FormControl,
  Modal,
  ModalFooter,
  ModalHeader,
  TextField
} from '@rodeo-dev/legos'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import IntlUtil from '../../../lib/intlutil.jsx'
import FormRow from './FormRow.js'
import ModalBody from './ModalBody.js'

const defaultState = {
  firstName: '',
  lastName: '',
  line1: '',
  city: '',
  state: '',
  country: '',
  postCode: '',
  vatNumber: ''
}

const inputFields = [
  [
    {
      name: 'firstName',
      label: 'setting.userForm.firstNamePlaceholder',
      placeholder: 'John'
    },
    {
      name: 'lastName',
      label: 'setting.userForm.lastNamePlaceholder',
      placeholder: 'Doe'
    }
  ],
  [
    {
      name: 'line1',
      label: 'setting.companyProfile.addressPlaceHolder'
    }
  ],
  [
    {
      name: 'postCode',
      label: 'setting.companyProfile.zipCodePlaceHolder'
    },
    {
      name: 'city',
      label: 'setting.companyProfile.cityPlaceHolder'
    }
  ],
  [
    {
      name: 'state',
      label: 'setting.companyProfile.statePlaceHolder'
    },
    {
      name: 'country',
      label: 'setting.companyProfile.countryPlaceHolder',
      editable: false
    }
  ],
  [
    {
      name: 'vatNumber',
      label: 'setting.companyProfile.vatNumberPlaceHolder',
      // the following can be "ALL" or an array of country codes like ['NL', 'UK']
      prependCountries: 'ALL'
    }
  ]
]

const shouldPrependCountryCode = (country, prependCountries) =>
  prependCountries &&
  (prependCountries === 'ALL' || prependCountries.indexOf(country) > -1)

const BillingAddressModalForm = ({
  open = false,
  onContinueClick,
  onCloseClick,
  team = { profile: defaultState },
  user = {},
  requiredInputs = [],
  hiddenInputs = [],
  billingInfo = {},
  disabled = false
}) => {
  // join data from user, team, and billing address (chargebee)
  const teamProfileDetails = useMemo(
    () => ({
      ...defaultState,
      firstName: user.firstName,
      lastName: user.lastName,
      line1: team.profile.street,
      postCode: team.profile.postcode,
      city: team.profile.city,
      state: team.profile.state,
      country: team.profile.country,
      vatNumber: team.profile.vatNo,
      ...billingInfo
    }),
    [team, user, billingInfo]
  )

  const [formValues, setFormValues] = useState(teamProfileDetails)

  // creates event callback for an input
  const setsState = useCallback(
    (inputName) => (e) =>
      setFormValues({
        ...formValues,
        [inputName]: e.target.value
      }),
    [formValues]
  )

  // clear state when modal is closed
  useEffect(() => {
    if (open) {
      setFormValues(teamProfileDetails)
    } else {
      setFormValues(defaultState)
    }
  }, [open, teamProfileDetails])

  // enable Continue when all required inputs are filled
  const isFormValid = useMemo(
    () =>
      open &&
      requiredInputs.reduce(
        (valid, input) => valid && Boolean(formValues[input]),
        true
      ),
    [formValues, open]
  )

  // to keep it DRY
  const renderTextField = useCallback(
    ({ name, label, placeholder, editable = true, prependCountries }) => (
      <TextField
        required={requiredInputs.includes(name)}
        label={IntlUtil.convertMessage(label)}
        placeholder={placeholder}
        value={formValues[name]}
        onChange={setsState(name)}
        disabled={disabled || !editable}
        {...(shouldPrependCountryCode(
          team.profile.country,
          prependCountries
        ) && {
          InputProps: {
            startAdornment: team.profile.country
          }
        })}
      />
    ),
    [requiredInputs, formValues, disabled, team]
  )

  const handleContinueClick = () => onContinueClick(formValues)

  return (
    <Modal open={open} onClose={onCloseClick}>
      <ModalHeader onClose={onCloseClick}>
        <span>
          {IntlUtil.convertMessage('setting.subscription.billingInfoTitle')}
        </span>
      </ModalHeader>
      <ModalBody>
        {inputFields.map((rowFields, rowIndex) => {
          // remove fields present in hiddenInputs
          const filteredRowFields = rowFields.filter(
            (fieldProps) => !hiddenInputs.includes(fieldProps.name)
          )
          const isFullWidth = filteredRowFields.length === 1

          // and return a FormControl for each input field
          const formControls = filteredRowFields.map(
            (fieldProps, fieldIndex) => (
              <FormControl
                key={`${fieldProps.name}${fieldIndex}`}
                fullWidth={isFullWidth}
              >
                {renderTextField(fieldProps)}
              </FormControl>
            )
          )

          if (formControls.length) {
            return <FormRow key={rowIndex}>{formControls}</FormRow>
          }

          return null
        })}
      </ModalBody>
      <ModalFooter>
        <Button
          color='secondary'
          onClick={handleContinueClick}
          disabled={!isFormValid || disabled}
        >
          {IntlUtil.convertMessage('common.common.confirmPositiveButton')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

BillingAddressModalForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onContinueClick: PropTypes.func.isRequired,
  onCloseClick: PropTypes.func.isRequired,
  team: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  requiredInputs: PropTypes.arrayOf(PropTypes.string),
  hiddenInputs: PropTypes.arrayOf(PropTypes.string),
  billingInfo: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    line1: PropTypes.string,
    line2: PropTypes.string,
    line3: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    stateCode: PropTypes.string,
    country: PropTypes.string,
    postCode: PropTypes.string,
    vatNumber: PropTypes.string
  }),
  disabled: PropTypes.bool
}

export default BillingAddressModalForm
